import { useMemo } from 'react';
import { useAppStore } from '../../../store/useAppStore';
import { ScheduleEvent } from '../../scheduler/types';
import { addLocalDays, getLocalDateKey } from './date-keys';

/**
 * Filters scheduler events to show only those the user is called to
 * 
 * Matches:
 * - Direct userId in attendees array
 * - Group tags like 'group:cast' where user belongs to that group
 */
export function useMyCalls(userId: string | null, userGroups: string[]): ScheduleEvent[] {
    const events = useAppStore(state => state.rehearsalSchedule || []);

    return useMemo(() => {
        if (!userId) return [];

        const now = new Date();
        const today = getLocalDateKey(now);

        // Get events in next 48 hours
        const endDate = getLocalDateKey(addLocalDays(now, 2));

        return events
            .filter(event => {
                // Filter by date range
                if (event.date < today || event.date > endDate) {
                    return false;
                }

                // No attendees means it's for everyone
                if (!event.attendees || event.attendees.length === 0) {
                    return true;
                }

                // Check if user is directly listed
                if (event.attendees.includes(userId)) {
                    return true;
                }

                // Check if user's groups are listed
                return event.attendees.some(attendee => {
                    if (attendee.startsWith('group:')) {
                        const groupName = attendee.slice(6);
                        return userGroups.includes(groupName);
                    }
                    return false;
                });
            })
            .toSorted((a, b) => {
                // Sort by date, then time
                const dateCompare = a.date.localeCompare(b.date);
                if (dateCompare !== 0) return dateCompare;
                return a.startTime.localeCompare(b.startTime);
            });
    }, [events, userId, userGroups]);
}
